import { BotDifficulty, RoomSettingConstants } from '@colonist/game-base-types';
import { UIRoomUserView } from './UIRoomUserView.ts';
import { clientI18n } from '../../ClientI18n.ts';
import { notificationController, socket } from '../../EntryShared.ts';
import { UIUtilsEjsVariables } from '../../UIUtilsEjsVariables.ts';
import { showHostActionOnGuestDialog } from '../../ui_popup/showHostActionOnGuestDialog.ts';
import { showSimpleDialog } from '../../ui_popup/showSimpleDialog.ts';
import { setElementInnerHTML } from '../../ui_utils/DomUtils.ts';
import { UIBrowserTabController } from '../UIBrowserTabController.ts';
import { ClientUserState } from '../../ClientUserState.ts';
import { LobbyImage } from '@colonist/types';
import { AnalyticEventCategory, AnalyticEventType, AnalyticEventValue, GiftMembershipMethod } from '../../../../../shared/entity_datas/EntityDatas.ts';
import { CA } from '../../analytics/Analytics.ts';
import { doActionIfCanGiftMembership, doActionIfLoggedIn } from '../../store/StoreUtils.ts';
import { navigateToPage } from '../../ui_utils/ClientUtils.ts';
import { getColonistUsername } from '../../ui_profile/UIUtilsProfile.ts';
import { UIUtilsFriends } from '../../ui_friends/UIUtilsFriends.ts';
import { UIViewProfilePopup } from '../../profile/UIViewProfilePopup.ts';
import { AudioManager, AudioType } from '@colonist/audio';
export class UIRoomUserList {
    clear() {
        setElementInnerHTML(this.playerListContainer, {
            key: 'strings:colonistCommon.utils.emptyString'
        });
        this.guestViews.splice(0, this.guestViews.length);
    }
    load(roomState) {
        this.hostView = new UIRoomUserView(this.playerListContainer);
        this.createGuestViews();
        this.update(roomState);
    }
    update(newRoomState) {
        this.roomState = newRoomState;
        this.updateHostView(newRoomState);
        this.updateGuestSessionViews(newRoomState);
        this.updateBotButtonViews(newRoomState);
    }
    // ====================================================================================================================================
    // Views
    // ====================================================================================================================================
    createGuestViews() {
        for(let i = 1; i < RoomSettingConstants.MAX_PLAYER_COUNT; i += 1){
            const newView = new UIRoomUserView(this.playerListContainer);
            this.guestViews.push(newView);
        }
    }
    updateHostView(roomState) {
        this.hostView.update(roomState.hostSession);
        this.hostView.updateActionIcons(UIRoomUserList.getActionIconsToDisplay(roomState, roomState.hostSession));
        this.hostView.setUsername(UIRoomUserList.getUsername(roomState, roomState.hostSession));
        const hostClickAction = this.playerClickAction(roomState.hostSession, roomState.hostSession);
        UIRoomUserList.togglePlayerHover(this.hostView, hostClickAction == undefined);
        this.hostView.setOnclick(()=>{
            AudioManager.play(AudioType.Click);
            hostClickAction === null || hostClickAction === void 0 ? void 0 : hostClickAction();
        });
        const canEditColor = UIRoomUserList.shouldShowColorSelectorBackground(roomState, roomState.hostSession);
        this.updateColorSelector(canEditColor, this.hostView);
    }
    updateGuestSessionViews(roomState) {
        const guestSessions = roomState.getGuestSessions();
        for (const [index, view] of this.guestViews.entries()){
            const guest = guestSessions[index];
            if (roomState.gameSetting.maxPlayers - 1 < index + 1) view.hideView();
            if (roomState.gameSetting.maxPlayers - 1 >= index + 1) view.showView();
            if (guest == undefined) {
                if (view.isShowingPlayer()) {
                    view.removePlayer();
                    UIBrowserTabController.blinkTab({
                        key: 'strings:roomPage.left',
                        options: {
                            count: roomState.getSessionCount(),
                            maxCount: roomState.gameSetting.maxPlayers
                        }
                    });
                    AudioManager.play(AudioType.LeaveRoom, UIRoomUserList.leaveSoundVolume);
                }
                continue;
            }
            if (!view.isShowingPlayer()) {
                UIBrowserTabController.blinkTab({
                    key: 'strings:roomPage.joined',
                    options: {
                        count: roomState.getSessionCount(),
                        maxCount: roomState.gameSetting.maxPlayers
                    }
                });
                AudioManager.play(AudioType.JoinRoom);
            }
            view.update(guest);
            const guestClickAction = this.playerClickAction(roomState.hostSession, guest);
            UIRoomUserList.togglePlayerHover(view, guestClickAction == undefined);
            view.setOnclick(()=>{
                AudioManager.play(AudioType.Click);
                guestClickAction === null || guestClickAction === void 0 ? void 0 : guestClickAction();
            });
            const canEditColor = UIRoomUserList.shouldShowColorSelectorBackground(roomState, guest);
            this.updateColorSelector(canEditColor, view);
            view.updateActionIcons(UIRoomUserList.getActionIconsToDisplay(roomState, guest));
            view.setBotDifficultyVisibility(guest.isBot);
            if (guest.isBot) {
                var _guest_botDifficulty;
                const botDifficulty = (_guest_botDifficulty = guest.botDifficulty) !== null && _guest_botDifficulty !== void 0 ? _guest_botDifficulty : BotDifficulty.BotMedium;
                const isHost = UIRoomUserList.isItMe(roomState.hostSession);
                if (isHost) {
                    view.renderBotDifficultyAsSegmentedControl(botDifficulty, (selectedDifficulty)=>{
                        socket.roomClient.setBotDifficulty(guest.roomSessionId, selectedDifficulty);
                    });
                } else {
                    view.renderBotDifficultyAsBadge(botDifficulty);
                }
            }
            view.setUsername(UIRoomUserList.getUsername(roomState, guest));
        }
    }
    updateBotButtonViews(roomState) {
        if (roomState.hostSession.userSessionId == socket.controller.session.id) {
            for (const view of this.guestViews){
                view.displayBotButton();
            }
            return;
        }
        for (const view of this.guestViews){
            view.hideBotButton();
        }
    }
    updateColorSelector(canEditColor, userView) {
        userView.setColorOnclick(canEditColor ? ()=>this.openEditProfileForPlayer(userView.state) : ()=>undefined);
        userView.toggleColorSelector(canEditColor);
    }
    // ====================================================================================================================================
    // Button Checks
    // ====================================================================================================================================
    static isItMe(session) {
        return socket.controller.session.id == session.userSessionId;
    }
    static isItCurrentUserId(session) {
        var _ClientUserState_userState;
        return ((_ClientUserState_userState = ClientUserState.userState) === null || _ClientUserState_userState === void 0 ? void 0 : _ClientUserState_userState.id) == session.userId;
    }
    static isItHost(roomState, session) {
        return roomState.hostSession.userSessionId == session.userSessionId;
    }
    static shouldShowColorSelectorBackground(roomState, session) {
        if (session.isBot && this.isItMe(roomState.hostSession)) return true;
        return this.isItMe(session);
    }
    // ====================================================================================================================================
    // Button Actions
    // ====================================================================================================================================
    playerClickAction(hostSession, playerSession) {
        const clickedOnBot = playerSession.isBot;
        if (clickedOnBot) return undefined;
        const clickedOnSelf = UIRoomUserList.isItMe(playerSession);
        if (clickedOnSelf) return ()=>this.openEditProfilePage(playerSession);
        const amIHost = UIRoomUserList.isItMe(hostSession);
        if (amIHost) return ()=>UIRoomUserList.showHostActionsPopup(playerSession);
        return ()=>UIRoomUserList.openProfilePage(playerSession);
    }
    openEditProfilePage(playerSession) {
        doActionIfLoggedIn(()=>this.openEditProfileForPlayer(playerSession));
    }
    static kickPlayer(playerSession) {
        socket.roomClient.kickPlayer(playerSession.roomSessionId);
    }
    static showHostActionsPopup(playerSession) {
        const viewProfileAction = async ()=>{
            const colonistUsername = await getColonistUsername(playerSession.username, playerSession.userId);
            UIViewProfilePopup.init(colonistUsername);
        };
        const kickAction = ()=>{
            UIRoomUserList.kickPlayer(playerSession);
        };
        showHostActionOnGuestDialog({
            username: playerSession.username,
            onKick: kickAction,
            onViewProfile: viewProfileAction
        });
    }
    static showKickPlayerPopup(playerSession) {
        const title = {
            key: 'strings:roomPage.hostActions.kick'
        };
        const body = {
            key: 'strings:roomPage.hostActions.kickPlayer',
            options: {
                username: playerSession.username
            }
        };
        const kickAction = ()=>{
            UIRoomUserList.kickPlayer(playerSession);
        };
        showSimpleDialog({
            title: title,
            body: body,
            onConfirm: kickAction,
            confirmLabel: {
                key: 'strings:roomPage.hostActions.kick'
            },
            showCancelBtn: true
        });
    }
    static async openProfilePage(playerSession) {
        const colonistUsername = await getColonistUsername(playerSession.username, playerSession.userId);
        UIViewProfilePopup.init(colonistUsername);
    }
    static getActionIconsToDisplay(roomState, session) {
        const actionIcons = [];
        const isCurrentUserSession = this.isItMe(session);
        const isCurrentUserId = this.isItCurrentUserId(session);
        const canGift = !session.isBot && !isCurrentUserSession && !isCurrentUserId && !UIUtilsEjsVariables.isUserOnDiscordMobile;
        if (this.canShowAddFriendIcon(session)) {
            actionIcons.push({
                icon: LobbyImage.AddFriend,
                action: async ()=>{
                    const colonistUsername = await getColonistUsername(session.username, session.userId);
                    if (this.alreadyFriends(colonistUsername)) {
                        notificationController.showNotification(LobbyImage.Friends, {
                            key: 'strings:popups.errors.friendError.alreadyFriends'
                        });
                        return;
                    }
                    if (this.alreadySentFriendRequest(colonistUsername)) {
                        notificationController.showNotification(LobbyImage.Friends, {
                            key: 'strings:popups.errors.friendError.alreadySentFriendRequest'
                        });
                        return;
                    }
                    const eventData = {
                        type: AnalyticEventType.Friends,
                        category: AnalyticEventCategory.Room,
                        floatValue: AnalyticEventValue.RoomAddFriendAction
                    };
                    void CA.sendAnalyticEvent(eventData);
                    void UIUtilsFriends.addFriendAction(colonistUsername);
                },
                tooltip: {
                    key: 'strings:game.playerOptionsPopup.addFriend'
                }
            });
        }
        if (canGift) {
            const giftButtonClickEventData = {
                type: AnalyticEventType.ABTestClickAnalytics,
                category: AnalyticEventCategory.GiftMembershipOverlay,
                floatValue: GiftMembershipMethod.Room
            };
            actionIcons.push({
                icon: LobbyImage.StoreGiftMembership,
                action: ()=>{
                    void CA.sendAnalyticEvent(giftButtonClickEventData);
                    doActionIfCanGiftMembership(()=>{
                        const giftAction = ()=>navigateToPage(`/store?username=${session.username}#gift`, true);
                        if (UIUtilsEjsVariables.isUserOnMobileAppOrWeb) {
                            showSimpleDialog({
                                title: {
                                    key: 'strings:roomPage.leaveRoom.title'
                                },
                                body: {
                                    key: 'strings:roomPage.leaveRoom.body'
                                },
                                onConfirm: giftAction,
                                showCancelBtn: true
                            });
                            return;
                        }
                        giftAction();
                    }, session.username);
                },
                tooltip: {
                    key: 'strings:game.tooltips.giftMembership',
                    options: {
                        username: session.username
                    }
                }
            });
        }
        if (isCurrentUserSession) actionIcons.push({
            icon: LobbyImage.Pencil,
            tooltip: {
                key: 'strings:homePage.header.editProfile'
            }
        });
        else if (this.isItMe(roomState.hostSession)) {
            const kickAction = session.isBot ? ()=>UIRoomUserList.kickPlayer(session) : ()=>UIRoomUserList.showKickPlayerPopup(session);
            actionIcons.push({
                icon: LobbyImage.XMark,
                action: kickAction,
                tooltip: {
                    key: 'strings:game.tooltips.kickPlayer',
                    options: {
                        username: session.username
                    }
                }
            });
        }
        return actionIcons;
    }
    static getUsername(roomState, session) {
        if (this.isItMe(session)) return `${session.username} ${clientI18n.t('strings:roomPage.you')}`;
        if (this.isItHost(roomState, session)) return `${session.username} ${clientI18n.t('strings:roomPage.host')}`;
        return session.username;
    }
    static togglePlayerHover(player, isDisabled) {
        if (isDisabled) {
            player.disableHover();
            return;
        }
        player.enableHover();
    }
    static canShowAddFriendIcon(session) {
        if (session.isBot) return false;
        if (UIRoomUserList.isItCurrentUserId(session)) return false;
        if (this.alreadyFriends(session.username)) return false;
        if (this.alreadySentFriendRequest(session.username)) return false;
        return true;
    }
    static alreadyFriends(username) {
        var _this_friendsData;
        var _this_friendsData_friends_some;
        return (_this_friendsData_friends_some = (_this_friendsData = this.friendsData) === null || _this_friendsData === void 0 ? void 0 : _this_friendsData.friends.some((friend)=>friend.username === username)) !== null && _this_friendsData_friends_some !== void 0 ? _this_friendsData_friends_some : false;
    }
    static alreadySentFriendRequest(username) {
        var _this_friendsData;
        var _this_friendsData_friendRequestsSent_some;
        return (_this_friendsData_friendRequestsSent_some = (_this_friendsData = this.friendsData) === null || _this_friendsData === void 0 ? void 0 : _this_friendsData.friendRequestsSent.some((friendRequest)=>friendRequest.username == username)) !== null && _this_friendsData_friendRequestsSent_some !== void 0 ? _this_friendsData_friendRequestsSent_some : false;
    }
    constructor(playerListContainer){
        this.guestViews = [];
        this.playerListContainer = playerListContainer;
        ClientUserState.registerChangeListener(async ()=>{
            UIRoomUserList.friendsData = await UIUtilsFriends.getFriends(ClientUserState.userState);
            if (this.roomState == undefined) return;
            this.update(this.roomState);
        });
    }
}
UIRoomUserList.leaveSoundVolume = 0.6;
